'use client';

import React, {
  ReactNode, createContext, useContext, useMemo,
} from 'react';
import { LegalGuaranteeContent } from '@/integrations/crystallize/helpers/legal-guarantee-shape-helper';

const LegalGuaranteeContext = createContext<LegalGuaranteeContent | null>(null);

/**
 * CMS-authored legal guarantee copy, or `null` when the document is unauthored or Crystallize is
 * unreachable. Returns `null` outside the provider too: every consumer already falls back to its
 * Weblate copy, so a missing provider must degrade rather than throw.
 */
export const useLegalGuaranteeContent = (): LegalGuaranteeContent | null =>
  useContext(LegalGuaranteeContext);

interface LegalGuaranteeProviderProps {
  content: LegalGuaranteeContent | null;
  children: ReactNode;
}

/**
 * Shares the single Legal Guarantee document — fetched once in the root layout — with every badge
 * placement, so the footer, product overview, checkout and order confirmation all read the same
 * CMS copy without refetching it per page.
 */
export function LegalGuaranteeProvider({
  content,
  children,
}: Readonly<LegalGuaranteeProviderProps>) {
  // The document changes only between requests, so memoizing keeps consumers from re-rendering on
  // every parent render.
  const value = useMemo(() => content, [content]);

  return <LegalGuaranteeContext.Provider value={value}>{children}</LegalGuaranteeContext.Provider>;
}
